import React, { useState, useEffect, useCallback } from 'react';

/** username → SHA-256(password) */
const VALID_CREDS: Record<string, string> = {
  vinayak: '193175fa2c63af9ba3208b50ae5bfc7ae549fd10ff587db85dec8025985a8d9e',
  dure: 'ef92b778bafe771e89245b89ecbc08a44a4e166c06659911881f383d4473e94f', // password123
};

async function sha256(str: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function todayISO() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}
function fmtDate(iso: string) {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

interface Recipient { id: number; name: string; phone_number: string; }
interface Schedule {
  id: number; name: string; interval_seconds: number;
  send_en: boolean; send_fr: boolean; contact_ids: number[];
  sender_id?: number | null;
  run_time?: string | null;
  timezone?: string | null;
  template_en?: string | null;
  template_en_lang?: string | null;
  template_fr?: string | null;
  template_fr_lang?: string | null;
  is_active: boolean; last_run_at: string | null; next_run_at: string | null;
}
interface SendResult { name: string; phone: string; lang: string; ok: boolean; message: string; template?: string; }
interface WaSender {
  id: number; name: string; phone_number?: string;
  api_key_masked?: string; default_template?: string | null;
  default_template_lang?: string;
  default_template_fr?: string | null;
  default_template_fr_lang?: string;
  has_key?: boolean;
}
interface WaTemplate {
  name: string; language: string; status?: string; category?: string;
}

const INTERVALS = [
  { label: '2 Minutes (test — from save)', value: 120 },
  { label: '5 Minutes (test — from save)', value: 300 },
  { label: '10 Minutes (test — from save)', value: 600 },
  { label: '30 Minutes (test — from save)', value: 1800 },
  { label: '1 Hour (test — from save)', value: 3600 },
  { label: '6 Hours (test — from save)', value: 21600 },
  { label: '12 Hours (test — from save)', value: 43200 },
  { label: '1 Day (uses At Time)', value: 86400 },
  { label: '2 Days (uses At Time)', value: 172800 },
  { label: '7 Days (uses At Time)', value: 604800 },
];

const COUNTRY_TIMEZONES: Record<string, string[]> = {
  'DR Congo': ['Africa/Kinshasa', 'Africa/Lubumbashi'],
  'Central African Republic': ['Africa/Bangui'],
  'Congo (Brazzaville)': ['Africa/Brazzaville'],
  'Uganda': ['Africa/Kampala'],
  'Rwanda': ['Africa/Kigali'],
  'Burundi': ['Africa/Bujumbura'],
  'Tanzania': ['Africa/Dar_es_Salaam'],
  'Kenya': ['Africa/Nairobi'],
  'Nigeria': ['Africa/Lagos'],
  'South Africa': ['Africa/Johannesburg'],
  'India': ['Asia/Kolkata'],
  'United Arab Emirates': ['Asia/Dubai'],
  'France': ['Europe/Paris'],
  'United Kingdom': ['Europe/London'],
  'United States': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'],
  'UTC': ['UTC'],
};

/** Backend root (no /api/v1). Prefer REACT_APP_API_URL host; fallback localhost. */
const API = (
  process.env.REACT_APP_INTERNAL_API_BASE ||
  (process.env.REACT_APP_API_URL || 'http://localhost:8001/api/v1').replace(/\/api\/v1\/?$/, '')
);

const c = {
  page: { fontFamily: "'Inter',-apple-system,sans-serif", background: '#0f1923', color: '#e2e8f0', minHeight: '100vh' } as React.CSSProperties,
  overlay: { position: 'fixed' as const, inset: 0, background: '#0a1628', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
  loginBox: { background: '#0f1923', border: '1px solid #1e3a5f', borderRadius: 12, padding: '40px 36px', width: 360, display: 'flex', flexDirection: 'column' as const, gap: 20 },
  badge: { background: '#006d5b', color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: '3px 8px', borderRadius: 4, display: 'inline-block' },
  topbar: { background: '#0a1628', borderBottom: '1px solid #1e3a5f', padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  tab: { padding: '8px 20px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' },
  tabActive: { background: '#006d5b', color: '#fff' },
  tabInact: { background: '#1e293b', color: '#64748b' },
  panel: { background: '#0a1628', borderRight: '1px solid #1e3a5f', padding: 24, display: 'flex', flexDirection: 'column' as const, gap: 20, overflowY: 'auto' as const, minHeight: 'calc(100vh - 53px)' },
  card: { background: '#1e293b', border: '1px solid #2d4a6b', borderRadius: 8, padding: 16 },
  sectionLbl: { fontSize: 10, fontWeight: 700, letterSpacing: 1.5, color: '#475569', textTransform: 'uppercase' as const, display: 'block', marginBottom: 8 },
  input: { background: '#1e293b', border: '1px solid #2d4a6b', color: '#e2e8f0', padding: '9px 12px', borderRadius: 6, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' as const },
  select: { background: '#1e293b', border: '1px solid #2d4a6b', color: '#e2e8f0', padding: '9px 12px', borderRadius: 6, fontSize: 13, outline: 'none', width: '100%' },
  btnGreen: { background: '#006d5b', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%' },
  btnBlue: { background: '#1e3a5f', border: '1px solid #2d4a6b', color: '#60a5fa', padding: '10px 16px', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', width: '100%' },
  btnRed: { background: 'none', border: '1px solid #7f1d1d', color: '#ef4444', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  btnSm: { background: '#1e293b', border: '1px solid #2d4a6b', color: '#94a3b8', padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  schedCard: { background: '#0f1923', border: '1px solid #1e3a5f', borderRadius: 8, padding: 16, display: 'flex', flexDirection: 'column' as const, gap: 10 },
  schedName: { fontSize: 14, fontWeight: 700, color: '#e2e8f0' },
  schedMeta: { fontSize: 12, color: '#64748b' },
  chkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '6px 0' },
  dotG: { width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 },
  dotR: { width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 },
};

export default function ReportSenderPage() {
  const [authed, setAuthed] = useState(sessionStorage.getItem('rpt_auth') === '1');
  const [authUser, setAuthUser] = useState(sessionStorage.getItem('rpt_user') || '');
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginErr, setLoginErr] = useState(false);
  const [tab, setTab] = useState<'manual' | 'schedules' | 'senders'>('manual');

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selRec, setSelRec] = useState<Set<number>>(new Set());
  const [sendEn, setSendEn] = useState(true);
  const [sendFr, setSendFr] = useState(true);
  const [reportDate, setReportDate] = useState('');
  const [generatedDate, setGenDate] = useState<string | null>(null);
  const [enUrl, setEnUrl] = useState<string | null>(null);
  const [frUrl, setFrUrl] = useState<string | null>(null);
  const [status, setStatus] = useState({ type: 'idle', text: 'Generate a preview to get started.' });
  const [results, setResults] = useState<SendResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [sendSenderId, setSendSenderId] = useState<string>('');
  const [sendTplEn, setSendTplEn] = useState('');
  const [sendTplFr, setSendTplFr] = useState('');

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [showNewSched, setShowNew] = useState(false);
  const [schedName, setSchedName] = useState('');
  const [schedInterval, setSchedInterval] = useState(86400);
  const [schedEn, setSchedEn] = useState(true);
  const [schedFr, setSchedFr] = useState(true);
  const [schedContacts, setSchedContacts] = useState<Set<number>>(new Set());
  const [schedStatus, setSchedStatus] = useState('');
  const [schedSenderId, setSchedSenderId] = useState<string>('');
  const [schedRunTime, setSchedRunTime] = useState('09:00');
  const [schedCountry, setSchedCountry] = useState('DR Congo');
  const [schedTz, setSchedTz] = useState('Africa/Kinshasa');
  const [schedTplEn, setSchedTplEn] = useState('');
  const [schedTplFr, setSchedTplFr] = useState('');
  const [formTemplates, setFormTemplates] = useState<WaTemplate[]>([]);

  const isTestInterval = schedInterval < 86400;
  const countryTzOptions = COUNTRY_TIMEZONES[schedCountry] || ['UTC'];

  const [senders, setSenders] = useState<WaSender[]>([]);
  const [sendersStore, setSendersStore] = useState('');
  const [senderStatus, setSenderStatus] = useState('');
  const [showSenderForm, setShowSenderForm] = useState(false);
  const [editingSenderId, setEditingSenderId] = useState<number | null>(null);
  const [senderName, setSenderName] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [senderKey, setSenderKey] = useState('');
  const [templatesSenderId, setTemplatesSenderId] = useState<number | null>(null);
  const [pulledTemplates, setPulledTemplates] = useState<WaTemplate[]>([]);
  const [tplPullStatus, setTplPullStatus] = useState('');
  const [tplStatus, setTplStatus] = useState('');
  const [tplName, setTplName] = useState('');
  const [tplLang, setTplLang] = useState('en');
  const [tplCategory, setTplCategory] = useState('UTILITY');
  const [tplHeaderFormat, setTplHeaderFormat] = useState('DOCUMENT');
  const [tplHeaderUrl, setTplHeaderUrl] = useState('');
  const [tplBody, setTplBody] = useState('');

  const effectiveDate = reportDate || todayISO();
  const enFilename = `Daily_Ebola_Report-${fmtDate(effectiveDate)}.pdf`;
  const frFilename = `Bulletin_quotidien_Ebola-${fmtDate(effectiveDate)}.pdf`;

  const loadRecipients = useCallback(async () => {
    const res = await fetch(`${API}/internal/api/recipients`);
    const data: Recipient[] = await res.json();
    setRecipients(data);
    setSelRec(new Set(data.map(r => r.id)));
  }, []);

  const loadSchedules = useCallback(async () => {
    const res = await fetch(`${API}/internal/api/schedules`);
    if (!res.ok) return;
    setSchedules(await res.json());
  }, []);

  const loadSenders = useCallback(async () => {
    const res = await fetch(`${API}/internal/api/wa-senders`);
    const data = await res.json();
    if (!res.ok) {
      setSenderStatus(data.detail || 'Failed to load senders');
      return;
    }
    setSenders(data.senders || []);
    setSendersStore(data.store || '');
  }, []);

  useEffect(() => {
    if (authed) {
      loadRecipients();
      loadSchedules();
      loadSenders();
    }
  }, [authed, loadRecipients, loadSchedules, loadSenders]);

  useEffect(() => {
    if (!authed) return;
    const t = setInterval(loadSchedules, 30000);
    return () => clearInterval(t);
  }, [authed, loadSchedules]);

  async function doLogin() {
    const user = loginUser.trim().toLowerCase();
    const hash = await sha256(loginPass);
    if (VALID_CREDS[user] && VALID_CREDS[user] === hash) {
      sessionStorage.setItem('rpt_auth', '1');
      sessionStorage.setItem('rpt_user', user);
      setAuthUser(user);
      setAuthed(true);
      setLoginErr(false);
    } else {
      setLoginErr(true);
      setLoginPass('');
    }
  }

  function doLogout() {
    sessionStorage.clear();
    setAuthed(false);
    setLoginPass('');
    setLoginErr(false);
  }

  function toggleRec(id: number) {
    setSelRec(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }
  function toggleSchedContact(id: number) {
    setSchedContacts(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  async function addRecipient() {
    if (!newName.trim() || !newPhone.trim()) return window.alert('Name and phone required');
    const res = await fetch(`${API}/internal/api/recipients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newName.trim(), phone_number: newPhone.trim() }),
    });
    if (res.ok) {
      setNewName('');
      setNewPhone('');
      await loadRecipients();
    } else {
      const e = await res.json();
      window.alert(e.detail || 'Failed');
    }
  }

  async function removeRecipient(id: number) {
    if (!window.confirm('Remove this recipient?')) return;
    await fetch(`${API}/internal/api/recipients/${id}`, { method: 'DELETE' });
    await loadRecipients();
  }

  async function generatePreviews() {
    setLoading(true);
    setStatus({ type: 'sending', text: 'Generating reports...' });
    setEnUrl(null);
    setFrUrl(null);
    const res = await fetch(`${API}/internal/api/generate-report?date=${effectiveDate}`, { method: 'POST' });
    const data = await res.json();
    if (!res.ok) {
      setStatus({ type: 'error', text: data.detail || 'Failed' });
      setLoading(false);
      return;
    }
    setGenDate(effectiveDate);
    setEnUrl(data.en_url);
    setFrUrl(data.fr_url);
    if (data.en_url) setTplHeaderUrl(data.en_url);
    setStatus({ type: 'done', text: `Reports generated for ${effectiveDate}. Select recipients and click Send.` });
    setLoading(false);
  }

  const loadTemplatesForSender = useCallback(async (sid: string) => {
    if (!sid) {
      setFormTemplates([]);
      setSendTplEn('');
      setSendTplFr('');
      setSchedTplEn('');
      setSchedTplFr('');
      return;
    }
    const res = await fetch(`${API}/internal/api/wa-senders/${sid}/templates`);
    const data = await res.json();
    const tpls: WaTemplate[] = res.ok ? (data.templates || []) : [];
    setFormTemplates(tpls);
    const sender = senders.find(s => String(s.id) === String(sid));
    if (sender?.default_template) {
      const v = `${sender.default_template}||${sender.default_template_lang || 'en'}`;
      setSendTplEn(v);
      setSchedTplEn(v);
    } else {
      setSendTplEn('');
      setSchedTplEn('');
    }
    if (sender?.default_template_fr) {
      const v = `${sender.default_template_fr}||${sender.default_template_fr_lang || 'fr'}`;
      setSendTplFr(v);
      setSchedTplFr(v);
    } else {
      setSendTplFr('');
      setSchedTplFr('');
    }
  }, [senders]);

  useEffect(() => {
    if (sendSenderId) loadTemplatesForSender(sendSenderId);
  }, [sendSenderId, loadTemplatesForSender]);

  useEffect(() => {
    if (schedSenderId) loadTemplatesForSender(schedSenderId);
  }, [schedSenderId, loadTemplatesForSender]);

  function parseTpl(v: string): { name: string | null; lang: string | null } {
    if (!v) return { name: null, lang: null };
    const i = v.indexOf('||');
    if (i < 0) return { name: v, lang: null };
    return { name: v.slice(0, i), lang: v.slice(i + 2) };
  }

  function tplSelect(value: string, onChange: (v: string) => void) {
    return (
      <select style={c.select} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Sender / .env default</option>
        {formTemplates.map(t => (
          <option key={`${t.name}-${t.language}`} value={`${t.name}||${t.language}`}>
            {t.name} ({t.language})
          </option>
        ))}
      </select>
    );
  }

  async function sendReports() {
    if (!generatedDate) return window.alert('Generate reports first');
    if (!sendEn && !sendFr) return window.alert('Select at least one report');
    if (selRec.size === 0) return window.alert('Select at least one recipient');
    setLoading(true);
    setResults([]);
    setStatus({ type: 'sending', text: `Sending to ${selRec.size} recipient(s)...` });
    const enT = parseTpl(sendTplEn);
    const frT = parseTpl(sendTplFr);
    const res = await fetch(`${API}/internal/api/send-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        date: generatedDate,
        recipient_ids: Array.from(selRec),
        send_en: sendEn,
        send_fr: sendFr,
        sender_id: sendSenderId ? Number(sendSenderId) : null,
        template_en: enT.name,
        template_en_lang: enT.lang,
        template_fr: frT.name,
        template_fr_lang: frT.lang,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setStatus({ type: 'error', text: data.detail || 'Send failed' });
      setLoading(false);
      return;
    }
    setResults(data.results || []);
    const sent = (data.results || []).filter((r: SendResult) => r.ok).length;
    const failed = (data.results || []).filter((r: SendResult) => !r.ok).length;
    setStatus({ type: failed ? 'error' : 'done', text: `Done — ${sent} sent, ${failed} failed` });
    setLoading(false);
  }

  async function createSchedule() {
    if (!schedName.trim()) return window.alert('Schedule name required');
    if (!schedEn && !schedFr) return window.alert('Select at least one report');
    if (schedContacts.size === 0) return window.alert('Select at least one contact');
    setSchedStatus('Creating...');
    const enT = parseTpl(schedTplEn);
    const frT = parseTpl(schedTplFr);
    const res = await fetch(`${API}/internal/api/schedules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: schedName,
        interval_seconds: schedInterval,
        run_time: schedRunTime,
        timezone: schedTz,
        send_en: schedEn,
        send_fr: schedFr,
        contact_ids: Array.from(schedContacts),
        sender_id: schedSenderId ? Number(schedSenderId) : null,
        template_en: enT.name,
        template_en_lang: enT.lang,
        template_fr: frT.name,
        template_fr_lang: frT.lang,
      }),
    });
    if (res.ok) {
      setSchedName('');
      setSchedInterval(86400);
      setSchedEn(true);
      setSchedFr(true);
      setSchedContacts(new Set());
      setSchedSenderId('');
      setSchedTplEn('');
      setSchedTplFr('');
      setShowNew(false);
      setSchedStatus('');
      await loadSchedules();
    } else {
      const e = await res.json();
      setSchedStatus(e.detail || 'Failed');
    }
  }

  async function toggleSchedule(id: number, active: boolean) {
    await fetch(`${API}/internal/api/schedules/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !active }),
    });
    await loadSchedules();
  }

  async function deleteSchedule(id: number) {
    if (!window.confirm('Delete this schedule?')) return;
    await fetch(`${API}/internal/api/schedules/${id}`, { method: 'DELETE' });
    await loadSchedules();
  }

  function openNewSender() {
    setEditingSenderId(null);
    setSenderName('');
    setSenderPhone('');
    setSenderKey('');
    setShowSenderForm(true);
  }

  function openEditSender(s: WaSender) {
    setEditingSenderId(s.id);
    setSenderName(s.name || '');
    setSenderPhone(s.phone_number || '');
    setSenderKey('');
    setShowSenderForm(true);
  }

  async function saveSender() {
    if (!senderName.trim()) return setSenderStatus('Name is required');
    if (!editingSenderId && !senderKey.trim()) return setSenderStatus('API key is required');
    let res: Response;
    if (editingSenderId) {
      const body: Record<string, string> = { name: senderName.trim(), phone_number: senderPhone.trim() };
      if (senderKey.trim()) body.api_key = senderKey.trim();
      res = await fetch(`${API}/internal/api/wa-senders/${editingSenderId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } else {
      res = await fetch(`${API}/internal/api/wa-senders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: senderName.trim(),
          phone_number: senderPhone.trim(),
          api_key: senderKey.trim(),
        }),
      });
    }
    const data = await res.json();
    if (!res.ok) {
      setSenderStatus(data.detail || 'Failed');
      return;
    }
    setSenderStatus('Sender saved.');
    setShowSenderForm(false);
    await loadSenders();
  }

  async function deleteSender(id: number) {
    if (!window.confirm('Delete this sender?')) return;
    const res = await fetch(`${API}/internal/api/wa-senders/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const data = await res.json();
      setSenderStatus(data.detail || 'Failed');
    }
    await loadSenders();
  }

  function openTemplates(id: number) {
    setTemplatesSenderId(id);
    setPulledTemplates([]);
    setTplPullStatus('');
    setTplStatus('');
    if (enUrl) setTplHeaderUrl(enUrl);
  }

  async function pullTemplates() {
    if (!templatesSenderId) return;
    setTplPullStatus('Pulling templates…');
    const res = await fetch(`${API}/internal/api/wa-senders/${templatesSenderId}/templates`);
    const data = await res.json();
    if (!res.ok) {
      setTplPullStatus(data.detail || 'Failed');
      return;
    }
    setPulledTemplates(data.templates || []);
    setTplPullStatus(`${(data.templates || []).length} template(s) found.`);
  }

  async function setTemplateAsDefault(t: WaTemplate, which: 'en' | 'fr' = 'en') {
    if (!templatesSenderId) return;
    const body = which === 'fr'
      ? { default_template_fr: t.name, default_template_fr_lang: t.language }
      : { default_template: t.name, default_template_lang: t.language };
    const res = await fetch(`${API}/internal/api/wa-senders/${templatesSenderId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) {
      setTplPullStatus(data.detail || 'Failed');
      return;
    }
    setTplPullStatus(`${which.toUpperCase()} default set to ${t.name} (${t.language}).`);
    await loadSenders();
  }

  async function createTemplate() {
    if (!templatesSenderId) return;
    if (!tplName.trim() || !tplBody.trim()) {
      setTplStatus('Template name and body text are required');
      return;
    }
    setTplStatus('Submitting to 360dialog…');
    const res = await fetch(`${API}/internal/api/wa-senders/${templatesSenderId}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: tplName.trim(),
        language: tplLang.trim() || 'en',
        category: tplCategory,
        body_text: tplBody.trim(),
        header_format: tplHeaderFormat,
        header_example_url: tplHeaderUrl.trim() || null,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      setTplStatus(typeof data.detail === 'string' ? data.detail : JSON.stringify(data));
      return;
    }
    setTplStatus('Template submitted — check 360dialog/Meta for approval status.');
  }

  function fmtInterval(s: number) {
    if (s < 3600) return `Every ${s / 60} min (test)`;
    if (s < 86400) return `Every ${s / 3600} hr (test)`;
    return `Every ${s / 86400} day${s / 86400 > 1 ? 's' : ''}`;
  }

  function schedLabel(s: Schedule) {
    const sec = Number(s.interval_seconds) || 86400;
    const tz = s.timezone || 'Africa/Kinshasa';
    if (sec < 86400) {
      if (sec >= 3600) return `Every ${sec / 3600} hr from save (test)`;
      return `Every ${sec / 60} min from save (test)`;
    }
    const days = sec / 86400;
    const freq = days === 1 ? 'Daily' : `Every ${days} days`;
    return `${freq} at ${s.run_time || '09:00'} (${tz})`;
  }

  function fmtTime(iso: string | null) {
    if (!iso) return 'Never';
    return new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleString();
  }

  function senderLabel(id?: number | null) {
    if (!id) return 'Default (.env)';
    const s = senders.find(x => x.id === id);
    return s ? s.name : `Sender #${id}`;
  }

  const senderOptions = (
    <>
      <option value="">Default (.env)</option>
      {senders.map(s => (
        <option key={s.id} value={s.id}>
          {s.name}{s.default_template ? ` — ${s.default_template}` : ''}
        </option>
      ))}
    </>
  );

  if (!authed) return (
    <div style={c.page}>
      <div style={c.overlay}>
        <div style={c.loginBox}>
          <span style={c.badge}>INTERNAL</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Ebola Report Sender</div>
            <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>Restricted access — authorized personnel only</div>
          </div>
          <div>
            <label style={{ ...c.sectionLbl }}>Username</label>
            <input style={c.input} type="text" value={loginUser} onChange={e => setLoginUser(e.target.value)} placeholder="Enter username" />
          </div>
          <div>
            <label style={{ ...c.sectionLbl }}>Password</label>
            <input style={c.input} type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} onKeyDown={e => e.key === 'Enter' && doLogin()} placeholder="Enter password" />
          </div>
          {loginErr && <div style={{ background: '#3b1a1a', border: '1px solid #7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 6, fontSize: 13 }}>Invalid username or password.</div>}
          <button style={{ ...c.btnGreen }} onClick={doLogin}>Sign In</button>
        </div>
      </div>
    </div>
  );

  const dotColor = status.type === 'sending' ? '#f59e0b' : status.type === 'done' ? '#22c55e' : status.type === 'error' ? '#ef4444' : '#475569';

  return (
    <div style={c.page}>
      <div style={c.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={c.badge}>INTERNAL</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#cbd5e1' }}>Ebola Voyage Santé+ — Report Sender</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={{ ...c.tab, ...(tab === 'manual' ? c.tabActive : c.tabInact) }} onClick={() => setTab('manual')}>Manual Send</button>
            <button style={{ ...c.tab, ...(tab === 'schedules' ? c.tabActive : c.tabInact) }} onClick={() => setTab('schedules')}>Auto Schedules</button>
            <button style={{ ...c.tab, ...(tab === 'senders' ? c.tabActive : c.tabInact) }} onClick={() => setTab('senders')}>WhatsApp Senders</button>
          </div>
          <span style={{ fontSize: 12, color: '#64748b', background: '#1e293b', padding: '4px 12px', borderRadius: 20 }}>{authUser}</span>
          <button style={{ background: 'none', border: '1px solid #2d4a6b', color: '#64748b', padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer' }} onClick={doLogout}>Sign Out</button>
        </div>
      </div>

      {tab === 'manual' && (
        <div style={{ display: 'grid', gridTemplateColumns: '340px 1fr', minHeight: 'calc(100vh - 53px)' }}>
          <div style={c.panel}>
            <div>
              <span style={c.sectionLbl}>Report Date</span>
              <input type="date" style={{ ...c.input }} value={reportDate} onChange={e => setReportDate(e.target.value)} />
            </div>
            <button style={{ ...c.btnBlue, opacity: loading ? 0.6 : 1 }} onClick={generatePreviews} disabled={loading}>Generate Report Preview</button>
            <div>
              <span style={c.sectionLbl}>Reports to Send</span>
              {[
                { lang: 'en', label: 'English Report', file: enFilename, sel: sendEn, set: setSendEn, badge: { background: '#1e3a5f', color: '#60a5fa' } },
                { lang: 'fr', label: 'French Report', file: frFilename, sel: sendFr, set: setSendFr, badge: { background: '#2d1f4a', color: '#c084fc' } },
              ].map(r => (
                <label key={r.lang} onClick={() => r.set((v: boolean) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#1e293b', border: `2px solid ${r.sel ? '#006d5b' : '#2d4a6b'}`, borderRadius: 8, padding: '12px 14px', cursor: 'pointer', marginBottom: 8 }}>
                  <input type="checkbox" checked={r.sel} onChange={() => r.set((v: boolean) => !v)} style={{ accentColor: '#006d5b', width: 16, height: 16 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{r.label} <span style={{ ...r.badge, fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, marginLeft: 6 }}>{r.lang.toUpperCase()}</span></div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{r.file}</div>
                  </div>
                </label>
              ))}
            </div>
            <div>
              <span style={c.sectionLbl}>WhatsApp Sender</span>
              <select style={c.select} value={sendSenderId} onChange={e => setSendSenderId(e.target.value)}>{senderOptions}</select>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div>
                  <span style={c.sectionLbl}>EN Template</span>
                  {tplSelect(sendTplEn, setSendTplEn)}
                </div>
                <div>
                  <span style={c.sectionLbl}>FR Template</span>
                  {tplSelect(sendTplFr, setSendTplFr)}
                </div>
              </div>
            </div>
            <div>
              <span style={c.sectionLbl}>Recipients <span style={{ color: '#006d5b', fontWeight: 700 }}>{selRec.size}/{recipients.length}</span></span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
                {recipients.length === 0
                  ? <div style={{ color: '#475569', fontSize: 12, padding: 8 }}>No recipients. Add below.</div>
                  : recipients.map(r => (
                    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#1e293b', border: '1px solid #2d4a6b', borderRadius: 6, padding: '9px 12px' }}>
                      <input type="checkbox" checked={selRec.has(r.id)} onChange={() => toggleRec(r.id)} style={{ accentColor: '#006d5b', width: 15, height: 15, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{r.phone_number}</div>
                      </div>
                      <button style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 18, lineHeight: 1 }} onClick={() => removeRecipient(r.id)}>×</button>
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <span style={c.sectionLbl}>Add Recipient</span>
              <input style={{ ...c.input, marginBottom: 8 }} type="text" placeholder="Name" value={newName} onChange={e => setNewName(e.target.value)} />
              <input style={{ ...c.input, marginBottom: 8 }} type="text" placeholder="+1234567890" value={newPhone} onChange={e => setNewPhone(e.target.value)} />
              <button style={{ ...c.btnBlue }} onClick={addRecipient}>+ Add to List</button>
            </div>
            <button style={{ ...c.btnGreen, opacity: (!generatedDate || loading || selRec.size === 0) ? 0.5 : 1 }} onClick={sendReports} disabled={!generatedDate || loading || selRec.size === 0}>Send Selected Reports</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', background: '#0f1923' }}>
            <div style={{ padding: '12px 24px', background: '#0a1628', borderBottom: '1px solid #1e3a5f', fontSize: 12, color: '#64748b', minHeight: 42, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
              <span>{status.text}</span>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 24px', gap: 16, overflowY: 'auto' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#94a3b8' }}>Report Preview</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, flex: 1 }}>
                {(['en', 'fr'] as const).map(lang => (
                  <div key={lang}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#475569', textTransform: 'uppercase', marginBottom: 8 }}>{lang === 'en' ? 'English' : 'French'}</div>
                    {(lang === 'en' ? enUrl : frUrl)
                      ? <iframe src={(lang === 'en' ? enUrl : frUrl)!} style={{ width: '100%', minHeight: 600, border: '1px solid #1e3a5f', borderRadius: 8 }} title={`${lang} report`} />
                      : <div style={{ minHeight: 600, border: '2px dashed #1e3a5f', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: 13, gap: 8 }}>Click Generate to preview</div>}
                  </div>
                ))}
              </div>
            </div>
            {results.length > 0 && (
              <div style={{ padding: '0 24px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {results.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '8px 12px', borderRadius: 6, background: '#1e293b', borderLeft: `3px solid ${r.ok ? '#22c55e' : '#ef4444'}` }}>
                    <span style={{ fontWeight: 600, flex: 1 }}>{r.name}</span>
                    <span style={{ color: '#64748b', fontSize: 12 }}>{r.phone} — {r.lang.toUpperCase()} — {r.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'schedules' && (
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900, margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0' }}>Auto Send Schedules</div>
            <button style={{ ...c.btnGreen, width: 'auto', padding: '8px 20px' }} onClick={() => setShowNew(v => !v)}>{showNewSched ? 'Cancel' : '+ New Schedule'}</button>
          </div>

          {showNewSched && (
            <div style={{ ...c.card, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>Create New Schedule</div>
              <div>
                <span style={c.sectionLbl}>Schedule Name</span>
                <input style={c.input} placeholder="e.g. Daily Morning Send" value={schedName} onChange={e => setSchedName(e.target.value)} />
              </div>
              <div>
                <span style={c.sectionLbl}>Send Every</span>
                <select style={c.select} value={schedInterval} onChange={e => setSchedInterval(Number(e.target.value))}>
                  {INTERVALS.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                </select>
              </div>
              <div style={{ opacity: isTestInterval ? 0.45 : 1, pointerEvents: isTestInterval ? 'none' : 'auto' }}>
                <span style={c.sectionLbl}>Run time (local) / Country &amp; Timezone</span>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 6 }}>
                  <input
                    style={c.input}
                    type="time"
                    value={schedRunTime}
                    disabled={isTestInterval}
                    onChange={e => setSchedRunTime(e.target.value)}
                  />
                  <select
                    style={c.select}
                    value={schedCountry}
                    disabled={isTestInterval}
                    onChange={e => {
                      const country = e.target.value;
                      setSchedCountry(country);
                      const zones = COUNTRY_TIMEZONES[country] || ['UTC'];
                      setSchedTz(zones[0]);
                    }}
                  >
                    {Object.keys(COUNTRY_TIMEZONES).map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  <select
                    style={c.select}
                    value={schedTz}
                    disabled={isTestInterval}
                    onChange={e => setSchedTz(e.target.value)}
                  >
                    {countryTzOptions.map(z => (
                      <option key={z} value={z}>
                        {z === 'Africa/Kinshasa' ? `${z} (DRC west — recommended)` :
                         z === 'Africa/Lubumbashi' ? `${z} (DRC east)` : z}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>
                  {isTestInterval
                    ? 'Test timing: sends every N minutes/hours starting from when you save. Run time / timezone are ignored.'
                    : 'Daily/multi-day: pick time + country/timezone. Example: 09:00 + DR Congo / Africa/Kinshasa.'}
                </div>
              </div>
              <div>
                <span style={c.sectionLbl}>WhatsApp Sender</span>
                <select style={c.select} value={schedSenderId} onChange={e => setSchedSenderId(e.target.value)}>{senderOptions}</select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <span style={c.sectionLbl}>EN Template</span>
                  {tplSelect(schedTplEn, setSchedTplEn)}
                </div>
                <div>
                  <span style={c.sectionLbl}>FR Template</span>
                  {tplSelect(schedTplFr, setSchedTplFr)}
                </div>
              </div>
              <div>
                <span style={c.sectionLbl}>Reports</span>
                <div style={{ display: 'flex', gap: 16 }}>
                  <label style={c.chkRow}><input type="checkbox" checked={schedEn} onChange={() => setSchedEn(v => !v)} style={{ accentColor: '#006d5b' }} /> English (EN)</label>
                  <label style={c.chkRow}><input type="checkbox" checked={schedFr} onChange={() => setSchedFr(v => !v)} style={{ accentColor: '#006d5b' }} /> French (FR)</label>
                </div>
              </div>
              <div>
                <span style={c.sectionLbl}>Send To ({schedContacts.size} selected)</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                  {recipients.map(r => (
                    <label key={r.id} style={{ ...c.chkRow, background: '#0f1923', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={schedContacts.has(r.id)} onChange={() => toggleSchedContact(r.id)} style={{ accentColor: '#006d5b', width: 15, height: 15 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{r.phone_number}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {schedStatus && <div style={{ fontSize: 13, color: '#fca5a5' }}>{schedStatus}</div>}
              <button style={c.btnGreen} onClick={createSchedule}>Create Schedule</button>
            </div>
          )}

          {schedules.length === 0
            ? <div style={{ ...c.card, textAlign: 'center', color: '#475569', padding: 40 }}>No schedules yet. Create one above.</div>
            : schedules.map(s => (
              <div key={s.id} style={c.schedCard}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={s.is_active ? c.dotG : c.dotR} />
                    <span style={c.schedName}>{s.name}</span>
                    <span style={{ fontSize: 11, background: '#1e293b', color: '#94a3b8', padding: '2px 8px', borderRadius: 4 }}>{schedLabel(s)}</span>
                    {s.send_en && <span style={{ fontSize: 10, background: '#1e3a5f', color: '#60a5fa', padding: '2px 6px', borderRadius: 4 }}>EN</span>}
                    {s.send_fr && <span style={{ fontSize: 10, background: '#2d1f4a', color: '#c084fc', padding: '2px 6px', borderRadius: 4 }}>FR</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...c.btnBlue, width: 'auto', padding: '6px 12px', fontSize: 12 }} onClick={() => toggleSchedule(s.id, s.is_active)}>
                      {s.is_active ? 'Pause' : 'Resume'}
                    </button>
                    <button style={c.btnRed} onClick={() => deleteSchedule(s.id)}>Delete</button>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' as const }}>
                  <span style={c.schedMeta}>Sender: {senderLabel(s.sender_id)}</span>
                  <span style={c.schedMeta}>EN: {s.template_en || 'default'}</span>
                  <span style={c.schedMeta}>FR: {s.template_fr || 'default'}</span>
                  <span style={c.schedMeta}>{s.contact_ids.length} contacts</span>
                  <span style={c.schedMeta}>Last: {fmtTime(s.last_run_at)}</span>
                  <span style={c.schedMeta}>Next: {fmtTime(s.next_run_at)}</span>
                </div>
              </div>
            ))}
        </div>
      )}

      {tab === 'senders' && (
        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 960, margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>WhatsApp senders</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>360dialog channels. Leave unset on send to use REPORT_SENDER_TOKEN from .env.</div>
              {sendersStore && <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Stored at: {sendersStore}</div>}
            </div>
            <button style={{ ...c.btnGreen, width: 'auto', padding: '8px 20px' }} onClick={openNewSender}>+ Add sender</button>
          </div>

          <div style={c.card}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Name', 'Phone', 'API key', 'EN default', 'FR default', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #1e3a5f', color: '#64748b', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {senders.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 16, color: '#64748b' }}>No senders yet.</td></tr>
                ) : senders.map(s => (
                  <tr key={s.id}>
                    <td style={{ padding: '10px 8px', borderBottom: '1px solid #1e3a5f' }}><strong>{s.name}</strong></td>
                    <td style={{ padding: '10px 8px', borderBottom: '1px solid #1e3a5f' }}>{s.phone_number || '—'}</td>
                    <td style={{ padding: '10px 8px', borderBottom: '1px solid #1e3a5f', color: '#94a3b8' }}>{s.api_key_masked || '—'}</td>
                    <td style={{ padding: '10px 8px', borderBottom: '1px solid #1e3a5f' }}>
                      {s.default_template ? `${s.default_template} (${s.default_template_lang || 'en'})` : <span style={{ color: '#64748b' }}>none</span>}
                    </td>
                    <td style={{ padding: '10px 8px', borderBottom: '1px solid #1e3a5f' }}>
                      {s.default_template_fr ? `${s.default_template_fr} (${s.default_template_fr_lang || 'fr'})` : <span style={{ color: '#64748b' }}>none</span>}
                    </td>
                    <td style={{ padding: '10px 8px', borderBottom: '1px solid #1e3a5f' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button style={c.btnSm} onClick={() => openEditSender(s)}>Edit</button>
                        <button style={c.btnSm} onClick={() => openTemplates(s.id)}>Templates</button>
                        <button style={c.btnRed} onClick={() => deleteSender(s.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showSenderForm && (
            <div style={{ ...c.card, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>{editingSenderId ? 'Edit sender' : 'New sender'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <span style={c.sectionLbl}>Display name</span>
                  <input style={c.input} value={senderName} onChange={e => setSenderName(e.target.value)} />
                </div>
                <div>
                  <span style={c.sectionLbl}>WABA phone</span>
                  <input style={c.input} value={senderPhone} onChange={e => setSenderPhone(e.target.value)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={c.sectionLbl}>D360-API-KEY</span>
                  <input style={c.input} type="password" value={senderKey} onChange={e => setSenderKey(e.target.value)} placeholder={editingSenderId ? 'Leave blank to keep existing key' : ''} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={{ ...c.btnGreen, width: 'auto' }} onClick={saveSender}>Save</button>
                <button style={c.btnSm} onClick={() => setShowSenderForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          {templatesSenderId != null && (
            <div style={{ ...c.card, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>
                Templates — {senders.find(s => s.id === templatesSenderId)?.name || templatesSenderId}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button style={{ ...c.btnBlue, width: 'auto' }} onClick={pullTemplates}>Pull templates from 360dialog</button>
                <span style={{ fontSize: 13, color: '#94a3b8' }}>{tplPullStatus}</span>
              </div>
              {pulledTemplates.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr>
                      {['Name', 'Language', 'Status', 'Category', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #1e3a5f', color: '#64748b', fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pulledTemplates.map((t, i) => (
                      <tr key={`${t.name}-${t.language}-${i}`}>
                        <td style={{ padding: 8, borderBottom: '1px solid #1e3a5f' }}>{t.name}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #1e3a5f' }}>{t.language}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #1e3a5f' }}>{t.status || '—'}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #1e3a5f' }}>{t.category || '—'}</td>
                        <td style={{ padding: 8, borderBottom: '1px solid #1e3a5f' }}>
                          <button style={c.btnSm} onClick={() => setTemplateAsDefault(t, 'en')}>EN default</button>
                          {' '}
                          <button style={c.btnSm} onClick={() => setTemplateAsDefault(t, 'fr')}>FR default</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div style={{ fontSize: 14, fontWeight: 700, color: '#60a5fa' }}>Create new template</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <span style={c.sectionLbl}>Template name</span>
                  <input style={c.input} value={tplName} onChange={e => setTplName(e.target.value)} />
                </div>
                <div>
                  <span style={c.sectionLbl}>Language</span>
                  <input style={c.input} value={tplLang} onChange={e => setTplLang(e.target.value)} />
                </div>
                <div>
                  <span style={c.sectionLbl}>Category</span>
                  <select style={c.select} value={tplCategory} onChange={e => setTplCategory(e.target.value)}>
                    <option value="UTILITY">Utility</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="AUTHENTICATION">Authentication</option>
                  </select>
                </div>
                <div>
                  <span style={c.sectionLbl}>Header type</span>
                  <select style={c.select} value={tplHeaderFormat} onChange={e => setTplHeaderFormat(e.target.value)}>
                    <option value="DOCUMENT">Document (PDF)</option>
                    <option value="NONE">None</option>
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={c.sectionLbl}>Header example PDF URL</span>
                  <input style={c.input} value={tplHeaderUrl} onChange={e => setTplHeaderUrl(e.target.value)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <span style={c.sectionLbl}>Body text</span>
                  <input style={c.input} value={tplBody} onChange={e => setTplBody(e.target.value)} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button style={{ ...c.btnGreen, width: 'auto' }} onClick={createTemplate}>Submit to 360dialog</button>
                <button style={c.btnSm} onClick={() => setTemplatesSenderId(null)}>Close</button>
              </div>
              {tplStatus && <div style={{ fontSize: 13, color: '#94a3b8' }}>{tplStatus}</div>}
            </div>
          )}

          {senderStatus && <div style={{ fontSize: 13, color: '#94a3b8' }}>{senderStatus}</div>}
        </div>
      )}
    </div>
  );
}
